export const getArray = (str) => {
  if (!str) return [];
  if (Array.isArray(str)) return str;
  try { return JSON.parse(str); } catch { return [str]; }
};

export const filterLearners = (employees, filters) => {
  return employees.filter(l => {
    const matchDept = !filters.department || filters.department === 'All' || l.department === filters.department;
    const matchLoc = !filters.location || filters.location === 'All' || l.location === filters.location;
    const matchMgr = !filters.manager || filters.manager === 'All' || l.manager === filters.manager;
    const matchLearner = !filters.learner || filters.learner === 'All' || String(l.id) === String(filters.learner);
    return matchDept && matchLoc && matchMgr && matchLearner;
  });
};

export const getLearnerCompletion = (assignments, learnerId, campaign) => {
  // Get assignments specific to this learner and this campaign
  const learnerAssignments = assignments.filter(a => 
    String(a.employee_id) === String(learnerId) && 
    String(a.campaign_id) === String(campaign.id)
  );
  
  if (learnerAssignments.length === 0) return 0;

  // Compare against campaign modules directly
  const campaignModules = getArray(campaign.trainingContent);
  const expectedCount = campaignModules.length > 0 ? campaignModules.length : Math.max(1, learnerAssignments.length);
  
  const completedCount = learnerAssignments.filter(a => a.status === 'Completed').length;
  
  return Math.min(100, Math.round((completedCount / expectedCount) * 100));
};

export const getCampaignCompletionReport = (raw, filters) => {
  const { employees = [], campaigns = [], assignments = [] } = raw || {};
  const filteredLearners = filterLearners(employees, filters);
  const filteredCampaigns = filters.campaign && filters.campaign !== 'All'
    ? campaigns.filter(c => String(c.id) === String(filters.campaign))
    : campaigns;

  return filteredCampaigns.map(c => {
    // Find all assignments linked to this campaign
    const campaignAssignments = assignments.filter(a => String(a.campaign_id) === String(c.id));
    
    // Determine assigned learners based on the assignments table AND the explicit assignedLearners list
    const assignedEmployeeIds = [...new Set([
      ...getArray(c.assignedLearners).map(String),
      ...campaignAssignments.map(a => String(a.employee_id))
    ])];

    const assigned = filteredLearners.filter(l => assignedEmployeeIds.includes(String(l.id)));
    
    let completed = 0;
    let inProgress = 0;
    let notStarted = 0;
    let overdue = 0;
    let completedLearners = 0;
    
    const endDate = new Date(c.endDate);
    const isPastDue = !isNaN(endDate) && endDate < new Date();

    assigned.forEach(l => {
      // Calculate completion based on learner completing all assignments
      const learnerAssignments = campaignAssignments.filter(a => String(a.employee_id) === String(l.id));
      const hasCompletedAll = learnerAssignments.length > 0 && learnerAssignments.every(a => a.status === 'Completed');
      if (hasCompletedAll) {
        completedLearners++;
      }
      
      const pct = getLearnerCompletion(assignments, l.id, c);
      
      if (pct === 100) {
        completed++;
      } else if (pct > 0) {
        inProgress++;
      } else {
        notStarted++;
      }

      if (pct < 100 && isPastDue) {
        overdue++;
      }
    });

    const avgCompletion = assigned.length ? Math.round((completedLearners / assigned.length) * 100) : 0;

    return {
      id: c.id,
      name: c.campaignName || c.title || 'Unnamed Campaign',
      type: c.campaignType || 'Mandatory',
      status: c.status || 'Active',
      startDate: c.startDate || '',
      endDate: c.endDate || '',
      totalAssigned: assigned.length,
      completed,
      inProgress,
      notStarted,
      overdue,
      avgCompletion,
    };
  });
};

export const getPhishingReport = (raw, filters) => {
  const { phishingSimulations = [] } = raw || {};
  return phishingSimulations.map(s => {
    const sent = s.sent || 0;
    const opened = s.opened || 0;
    const clicked = s.clicked || 0;
    const reported = s.reported || 0;
    const submitted = 0;
    return {
      id: s.id,
      name: s.name || 'Unnamed Simulation',
      sendDate: s.date || '',
      status: s.status || 'Completed',
      sent,
      opened,
      clicked,
      submitted,
      reported,
      clickRate: sent ? Math.round((clicked / sent) * 100) : 0,
      reportRate: sent ? Math.round((reported / sent) * 100) : 0,
      credentialSubmissionRate: 0,
    };
  });
};

export const getDepartmentRiskReport = (raw, filters) => {
  const { employees = [], auditLogs = [] } = raw || {};
  const filteredLearners = filterLearners(employees, filters);
  const depts = [...new Set(filteredLearners.map(l => l.department).filter(Boolean))];

  return depts.map(dept => {
    const dl = filteredLearners.filter(l => l.department === dept);
    const avgRisk = dl.length ? Math.round(dl.reduce((a, b) => a + (b.phishingRiskScore || 0), 0) / dl.length) : 0;
    const highRisk = dl.filter(l => (l.phishingRiskScore || 0) >= 50).length;
    const medRisk = dl.filter(l => (l.phishingRiskScore || 0) >= 20 && (l.phishingRiskScore || 0) < 50).length;
    const lowRisk = dl.filter(l => (l.phishingRiskScore || 0) < 20).length;
    
    const deptAuditLogs = auditLogs.filter(a => dl.some(l => String(l.id) === String(a.user_id)));
    const clicks = deptAuditLogs.filter(a => a.action === 'Phishing Click' || a.action === 'click').length;
    const reports = deptAuditLogs.filter(a => a.action === 'Phishing Report' || a.action === 'report').length;

    return { dept, total: dl.length, avgRisk, highRisk, medRisk, lowRisk, clicks, reports };
  }).sort((a, b) => b.avgRisk - a.avgRisk);
};

export const getComplianceReport = (raw, filters) => {
  const { complianceRecords = [], employees = [], campaigns = [] } = raw || {};
  const filteredLearners = filterLearners(employees, filters);
  
  return complianceRecords
    .filter(r => filteredLearners.some(l => String(l.id) === String(r.user_id)))
    .map(r => {
      const learner = filteredLearners.find(l => String(l.id) === String(r.user_id));
      const campaign = campaigns.find(c => String(c.id) === String(r.requirement));
      return {
        id: r.id,
        learnerName: learner ? `${learner.firstName} ${learner.lastName}` : r.user_id,
        department: learner?.department || '—',
        manager: learner?.manager || '—',
        training: campaign?.campaignName || r.requirement || 'Unknown',
        assignedDate: r.created_at || '—',
        dueDate: r.next_audit || '—',
        completionDate: r.status === 'Compliant' ? r.next_audit : '—',
        status: r.status || 'Not Started',
      };
    });
};

export const getLearnerActivityReport = (raw, filters) => {
  const { employees = [], campaigns = [], assignments = [], auditLogs = [], policyAcks = [], moduleAcks = [] } = raw || {};
  const filteredLearners = filterLearners(employees, filters);
  
  return filteredLearners.map(l => {
    const learnerAuditLogs = auditLogs.filter(a => String(a.user_id) === String(l.id));
    
    const learnerCampaigns = campaigns.filter(c => {
      const explicit = getArray(c.assignedLearners).map(String).includes(String(l.id));
      const hasAssignment = assignments.some(a => String(a.campaign_id) === String(c.id) && String(a.employee_id) === String(l.id));
      return explicit || hasAssignment;
    });
    
    const totalCampaigns = learnerCampaigns.length;
    
    let completedCampaigns = 0;
    let totalPct = 0;
    
    learnerCampaigns.forEach(c => {
      const pct = getLearnerCompletion(assignments, l.id, c);
      totalPct += pct;
      if (pct === 100) completedCampaigns++;
    });

    const avgCompletion = totalCampaigns ? Math.round(totalPct / totalCampaigns) : 0;

    const learnerAssns = assignments.filter(a => String(a.employee_id) === String(l.id) && a.status === 'Completed' && a.completed_at);
    let lastTrainingDate = l.lastTrainingDate || '—';
    if (learnerAssns.length > 0) {
      const dates = learnerAssns.map(a => new Date(a.completed_at).getTime()).filter(t => !isNaN(t));
      if (dates.length > 0) {
        lastTrainingDate = new Date(Math.max(...dates)).toLocaleDateString();
      }
    }

    const learnerPolAcks = policyAcks.filter(a => String(a.employee_id) === String(l.id));
    const learnerModAcks = moduleAcks.filter(a => String(a.employee_id) === String(l.id));

    return {
      id: l.id,
      name: `${l.firstName || ''} ${l.lastName || ''}`.trim() || l.email,
      email: l.email || '—',
      department: l.department || '—',
      manager: l.manager || '—',
      location: l.location || '—',
      trainingStatus: l.trainingStatus || 'Compliant',
      riskScore: l.phishingRiskScore || 0,
      lastTrainingDate,
      totalCampaigns,
      completedCampaigns,
      avgCompletion,
      phishingClicks: learnerAuditLogs.filter(a => a.action === 'Phishing Click' || a.action === 'click').length,
      phishingReports: learnerAuditLogs.filter(a => a.action === 'Phishing Report' || a.action === 'report').length,
      credentialSubmissions: learnerAuditLogs.filter(a => a.action === 'Credential Submission' || a.action === 'submission').length,
      securityAcks: learnerModAcks.length,
      policyAcks: learnerPolAcks.length,
      hasSecurityAck: learnerModAcks.length > 0 ? 'Yes' : 'No',
      hasPolicyAck: learnerPolAcks.length > 0 ? 'Yes' : 'No',
      lastPhishingResult: l.lastPhishingResult || '—',
    };
  });
};

export const getOverdueReport = (raw, filters) => {
  const { employees = [], campaigns = [], assignments = [] } = raw || {};
  const filteredLearners = filterLearners(employees, filters);
  const rows = [];

  campaigns.forEach(c => {
    if (filters.campaign && filters.campaign !== 'All' && String(c.id) !== String(filters.campaign)) return;
    
    const campaignAssignments = assignments.filter(a => String(a.campaign_id) === String(c.id));
    const assignedEmployeeIds = [...new Set([
      ...getArray(c.assignedLearners).map(String),
      ...campaignAssignments.map(a => String(a.employee_id))
    ])];
    
    const endDate = new Date(c.endDate);
    const isPastDue = !isNaN(endDate) && endDate < new Date();

    filteredLearners
      .filter(l => assignedEmployeeIds.includes(String(l.id)))
      .forEach(l => {
        const pct = getLearnerCompletion(assignments, l.id, c);
        
        if (pct < 100 && isPastDue) {
          rows.push({
            learnerId: l.id,
            learnerName: `${l.firstName || ''} ${l.lastName || ''}`.trim() || l.email,
            email: l.email || '—',
            department: l.department || '—',
            manager: l.manager || '—',
            campaign: c.campaignName || 'Unnamed Campaign',
            campaignType: c.campaignType || 'Mandatory',
            dueDate: c.endDate || '—',
            completion: pct,
            daysOverdue: !isNaN(endDate) ? Math.max(0, Math.floor((new Date() - endDate) / (1000 * 60 * 60 * 24))) : 0,
          });
        }
      });
  });

  return rows;
};

export const getAuditLogReport = (raw, filters) => {
  const { auditLogs = [], employees = [] } = raw || {};
  const filteredLearners = filterLearners(employees, filters);
  const learnerIds = new Set(filteredLearners.map(l => String(l.id)));
  
  return auditLogs
    .filter(log => !log.user_id || learnerIds.has(String(log.user_id)))
    .map(log => {
      if (log.user_id && !learnerIds.has(String(log.user_id))) return null;
      const learner = employees.find(e => String(e.id) === String(log.user_id));
      return {
        id: log.id,
        date: log.created_at || log.timestamp || '—',
        learnerName: learner ? `${learner.firstName || ''} ${learner.lastName || ''}`.trim() || learner.email : (log.user_id ? 'Unknown Learner' : 'System'),
        department: learner?.department || '—',
        action: log.action || '—',
        details: log.details || log.description || '—',
        ipAddress: log.ip_address || '—'
      };
    })
    .filter(Boolean)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
};